/**
 * NestJS AllExceptionsFilter wraps HttpException.getResponse() under `error`.
 * That value is either a string or `{ message, error, statusCode }`.
 * Default Nest responses put `message` at the top level instead.
 */
export function collectErrorMessages(value: unknown): string[] {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectErrorMessages);
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;

    if (record.message !== undefined) {
      const fromMessage = collectErrorMessages(record.message);
      if (fromMessage.length > 0) {
        return fromMessage;
      }
    }

    if (record.error !== undefined) {
      return collectErrorMessages(record.error);
    }
  }

  return [];
}

export async function getApiErrorMessage(response: Response): Promise<string> {
  const fallback = `요청이 실패했습니다 (${response.status} ${response.statusText})`.trim();
  const raw = await response.text();

  if (!raw) {
    return fallback;
  }

  try {
    const body: unknown = JSON.parse(raw);
    const messages = collectErrorMessages(body);
    if (messages.length > 0) {
      return messages.join("\n");
    }
    return JSON.stringify(body, null, 2);
  } catch {
    return raw;
  }
}

export function getErrorMessageFromUnknown(
  error: unknown,
  fallback: string = "알 수 없는 오류가 발생했습니다"
): string {
  if (error instanceof Error) {
    try {
      const parsed: unknown = JSON.parse(error.message);
      const messages = collectErrorMessages(parsed);
      if (messages.length > 0) {
        return messages.join("\n");
      }
    } catch {
      // error.message is not JSON
    }

    return error.message || fallback;
  }

  const messages = collectErrorMessages(error);
  if (messages.length > 0) {
    return messages.join("\n");
  }

  if (typeof error === "string" && error.trim()) {
    return error;
  }

  return fallback;
}
